const LEARN_GUIDE_SEARCH_STOP_WORDS = new Set([
  "a",
  "al",
  "como",
  "con",
  "de",
  "del",
  "el",
  "en",
  "la",
  "las",
  "lo",
  "los",
  "o",
  "para",
  "por",
  "que",
  "sin",
  "un",
  "una",
  "y",
]);

const LEARN_GUIDE_BROAD_TOKENS = new Set([
  "aprende",
  "aprender",
  "funcion",
  "funciones",
  "guia",
  "guias",
  "juego",
  "juegos",
  "jugar",
  "modo",
  "modos",
  "pagina",
  "paginas",
  "xima",
]);

const LEARN_GUIDE_SEARCH_SYNONYMS = {
  juego: ["juego", "juegos", "jugar", "partida", "partidas"],
  juegos: ["juego", "juegos", "jugar", "partida", "partidas"],
  jugar: ["juego", "juegos", "jugar", "partida", "partidas"],
  partida: ["partida", "partidas", "juego", "jugar"],
  partidas: ["partida", "partidas", "juego", "jugar"],
  unirme: ["unirme", "unirse", "unir", "unete"],
  unirse: ["unirse", "unirme", "unir", "unete"],
  unir: ["unir", "unirme", "unirse", "unete"],
  voz: ["voz", "microfono", "audio"],
  oz: ["voz"],
  control: ["control", "controles"],
  controles: ["control", "controles"],
  entrar: ["entrar", "unirse", "unirme", "unir"],
  ingresar: ["ingresar", "entrar", "unirse", "unirme"],
  microfono: ["microfono", "voz", "audio"],
  celular: ["celular", "movil", "telefono"],
  movil: ["movil", "celular", "telefono"],
  telefono: ["telefono", "movil", "celular"],
  replay: ["replay", "repeticion", "reproducir"],
  repeticion: ["repeticion", "replay", "reproducir"],
  sesion: ["sesion", "login", "entrar"],
};

export const normalizeLearnGuideSearchText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const getSingularToken = (token) => {
  if (token.length > 4 && token.endsWith("es")) return token.slice(0, -2);
  if (token.length > 3 && token.endsWith("s")) return token.slice(0, -1);
  return token;
};

export const tokenizeLearnGuideSearch = (value, { keepStopWords = false } = {}) =>
  normalizeLearnGuideSearchText(value)
    .split(/[^a-z0-9]+/)
    .filter((token) => token.length > 1 && (keepStopWords || !LEARN_GUIDE_SEARCH_STOP_WORDS.has(token)));

export const getLearnGuideSearchTokens = (value) => tokenizeLearnGuideSearch(value);

const getQueryTokenVariants = (queryToken) => {
  const variants = new Map();
  const addVariant = (token, weight = 1) => {
    if (!token) return;
    variants.set(token, Math.max(variants.get(token) || 0, weight));
  };

  addVariant(queryToken, 1);
  addVariant(getSingularToken(queryToken), 0.95);

  (LEARN_GUIDE_SEARCH_SYNONYMS[queryToken] || []).forEach((token) => {
    addVariant(token, token === queryToken ? 1 : 0.8);
    addVariant(getSingularToken(token), 0.76);
  });

  return Array.from(variants, ([token, weight]) => ({ token, weight }));
};

export const getLearnGuideSearchDistance = (leftToken, rightToken) => {
  const left = String(leftToken || "");
  const right = String(rightToken || "");
  if (left === right) return 0;
  if (!left) return right.length;
  if (!right) return left.length;

  const previousRow = Array.from({ length: right.length + 1 }, (_, index) => index);
  const currentRow = new Array(right.length + 1);

  for (let leftIndex = 0; leftIndex < left.length; leftIndex += 1) {
    currentRow[0] = leftIndex + 1;
    for (let rightIndex = 0; rightIndex < right.length; rightIndex += 1) {
      const insertionCost = currentRow[rightIndex] + 1;
      const deletionCost = previousRow[rightIndex + 1] + 1;
      const substitutionCost = previousRow[rightIndex] + (left[leftIndex] === right[rightIndex] ? 0 : 1);
      currentRow[rightIndex + 1] = Math.min(insertionCost, deletionCost, substitutionCost);
    }
    previousRow.splice(0, previousRow.length, ...currentRow);
  }

  return previousRow[right.length];
};

const scoreLearnGuideSearchToken = (queryToken, itemToken) => {
  if (!queryToken || !itemToken) return 0;
  if (queryToken === itemToken) return 1;
  if (getSingularToken(queryToken) === getSingularToken(itemToken)) return 0.88;
  if (queryToken.length < 4 || itemToken.length < 4) return 0;

  const distanceLimit = queryToken.length >= 7 ? 2 : 1;
  return getLearnGuideSearchDistance(queryToken, itemToken) <= distanceLimit ? 0.52 : 0;
};

const getSearchFields = (item) => [
  { text: item.label, weight: 58 },
  { text: item.path, weight: 42 },
  { text: item.searchPrimaryText || "", weight: 34 },
  { text: item.searchSecondaryText || item.searchText || "", weight: 10 },
];

export const scoreLearnGuideSearchItem = (item, queryTokens) => {
  if (!queryTokens.length) {
    return { score: 0, matchedSpecific: false, matchedTokenCount: 0 };
  }

  let score = 0;
  let matchedSpecific = false;
  let matchedTokenCount = 0;

  queryTokens.forEach((queryToken, queryTokenIndex) => {
    const queryTokenIsBroad = LEARN_GUIDE_BROAD_TOKENS.has(queryToken);
    const queryWeight = (queryTokenIndex === 0 ? 1.25 : 1) * (queryTokenIsBroad ? 0.42 : 1);
    const tokenVariants = getQueryTokenVariants(queryToken);

    let bestTokenScore = 0;
    getSearchFields(item).forEach((field) => {
      const fieldTokens = tokenizeLearnGuideSearch(field.text, { keepStopWords: true });
      fieldTokens.forEach((itemToken) => {
        tokenVariants.forEach((variant) => {
          const tokenScore = scoreLearnGuideSearchToken(variant.token, itemToken);
          bestTokenScore = Math.max(bestTokenScore, tokenScore * variant.weight * field.weight);
        });
      });
    });

    if (bestTokenScore > 0) {
      matchedTokenCount += 1;
      if (!queryTokenIsBroad) matchedSpecific = true;
    }

    score += bestTokenScore * queryWeight;
  });

  return { score, matchedSpecific, matchedTokenCount };
};

export const getLearnGuideSearchResults = (groups, searchTerm, { limit = 24 } = {}) => {
  const queryTokens = getLearnGuideSearchTokens(searchTerm);
  const hasSpecificQueryToken = queryTokens.some((token) => !LEARN_GUIDE_BROAD_TOKENS.has(token));
  if (!queryTokens.length) return [];

  return groups
    .flatMap((group) =>
      group.items.map((item) => {
        const result = scoreLearnGuideSearchItem(item, queryTokens);
        return {
          ...item,
          groupLabel: group.label,
          matchedSpecific: result.matchedSpecific,
          matchedTokenCount: result.matchedTokenCount,
          searchScore: result.score,
        };
      })
    )
    .filter((item) => {
      if (item.searchScore < 10) return false;
      if (hasSpecificQueryToken && !item.matchedSpecific) return false;
      return true;
    })
    .sort((leftItem, rightItem) => {
      if (rightItem.matchedSpecific !== leftItem.matchedSpecific) {
        return Number(rightItem.matchedSpecific) - Number(leftItem.matchedSpecific);
      }
      return (
        rightItem.searchScore - leftItem.searchScore ||
        rightItem.matchedTokenCount - leftItem.matchedTokenCount ||
        leftItem.label.localeCompare(rightItem.label)
      );
    })
    .slice(0, limit);
};
